import * as React from 'react';

import { cn } from '../../lib/utils';

// Minimal stand-in for class-variance-authority (its ESM build does not load under this webpack 4 setup).
const cva = (base, { variants, defaultVariants }) => ({ variant, size, className } = {}) =>
	[
		base,
		variants.variant[variant || defaultVariants.variant],
		variants.size[size || defaultVariants.size],
		className,
	]
		.filter(Boolean)
		.join(' ');

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
				destructive:
					'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
				outline:
					'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
				secondary:
					'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
				signal:
					'bg-signal text-signal-foreground shadow-card hover:brightness-105 font-semibold',
				onInk:
					'border border-ink-foreground/25 bg-ink-foreground/5 text-ink-foreground hover:bg-ink-foreground/15',
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				lg: 'h-11 rounded-md px-7 text-[0.95rem]',
				icon: 'h-9 w-9',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	}
);

// asChild: merge the button styles onto the single child element (e.g. <a>, <Link>)
// instead of rendering a <button>.
const Button = React.forwardRef(
	({ className, variant, size, asChild = false, children, ...props }, ref) => {
		const classes = cn(buttonVariants({ variant, size, className }));
		if (asChild && React.isValidElement(children)) {
			return React.cloneElement(children, {
				...props,
				ref,
				className: cn(classes, children.props.className),
			});
		}
		return (
			<button className={classes} ref={ref} {...props}>
				{children}
			</button>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
